import { filterXSS } from "xss";
import { cn } from "@/components/ui/cn";
import { embedYouTube } from "@/lib/embeds/youtube";

// Strip <script>/<style> entirely (tag + contents) rather than escaping them to
// visible text; everything else falls back to the xss default whitelist, which
// keeps editorial rich-text tags (headings, lists, links, images, tables…) and
// removes event-handler attributes, javascript: URIs, and unknown tags.
const SANITIZE_OPTS = { stripIgnoreTagBody: ["script", "style"] as string[] };

/**
 * Renders server-supplied rich-text HTML safely:
 *  1. `xss` sanitizes the (untrusted) backend HTML — drops <script>/<style>,
 *     on* handlers, javascript: URIs, and any non-whitelisted tag. Pure JS, so
 *     it runs both server-side and in the browser (RichHtml is used from server
 *     components AND the EventRecordingPlayer client component) with no jsdom.
 *  2. embedYouTube THEN converts bare/anchored YouTube links into our own
 *     hardcoded youtube-nocookie iframe. Order matters: the sanitizer strips
 *     <iframe>, so we add our trusted embed AFTER sanitizing rather than
 *     whitelisting iframes (which would let any backend iframe through).
 *
 * Use this anywhere the portal returns rich-text HTML (course intros, lecture
 * descriptions, FAQs, syllabi, event recordings). The styling reuses the
 * `prose-rich` class so type/links match the rest of the site.
 */
export function RichHtml({
  html,
  className,
}: {
  html: string | null | undefined;
  className?: string;
}) {
  if (!html) return null;
  const clean = filterXSS(html, SANITIZE_OPTS);
  const processed = lazyImages(embedYouTube(clean));
  return (
    <div className={cn("prose-rich", className)} dangerouslySetInnerHTML={{ __html: processed }} />
  );
}

/** Body pictures load lazily and decode off the main thread — every one sits
 *  below the page's own hero, so none is the largest paint. Added after
 *  sanitizing (the sanitizer keeps only the attributes it knows); an image
 *  that already says how to load is left alone. */
function lazyImages(html: string): string {
  return html.replace(/<img\b(?![^>]*\bloading=)/gi, '<img loading="lazy" decoding="async"');
}
